import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ResponsiveService } from '@services/responsive.service';

@Component({
  selector: 'ark-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
})
export class SliderComponent implements OnInit, OnDestroy {
  @ViewChild('slider', { static: false }) slider: ElementRef | undefined;
  @ViewChild('bar', { static: false }) bar: ElementRef | undefined;
  @ViewChild('knob', { static: false }) knob: ElementRef | undefined;
  @Input() inGrid = false;
  @Input() textHidden = false;
  @Input() showKnob = true;
  @Input() fillColor = '';
  @Input() blocked = 0;
  @Input() max!: number;
  @Input() points!: { label: string; value: number }[];
  activePoint?: { label: string; value: number };
  fillCurrentLevel = 0;
  fillDefaultLevel = 0;
  currentLeft = 0;
  knobPositionX = 0;
  knobDragActive = false;
  levelDisplay = 0;
  calcLeft = 0;
  calcWidth = 0;
  knobWidth = 0;
  valuesSet = false;
  mousemoveListener: any = null;
  mouseupListener: any = null;
  notchValue = 0;
  levelStore = 0;
  responsiveSubscription: Subscription | undefined;
  checkmarkShown = false;

  @Output() dragEnd = new EventEmitter();
  @Output() dragFinished = new EventEmitter();
  @Output() pointSelected = new EventEmitter<number>();
  @Input() hasCommitted!: boolean;

  constructor(
    private renderer: Renderer2,
    private responsive: ResponsiveService,
  ) {
    this.responsive.resize();
    this.responsiveSubscription = this.responsive.screenSize.subscribe(
      (size) => {
        if (size !== null && this.valuesSet) {
          this.initPositions(this.levelStore);
          this.calculatePositions();
        }
      },
    );

    // environment.log(this.levelDisplay)
  }

  @Input() set current(value: number | undefined) {
    if (value || value === 0) {
      setTimeout(() => {
        this.initPositions(value);
        this.calculatePositions();
        this.valuesSet = true;
      }, 100);
    }
  }

  @Input() set reference(value: number | undefined) {
    if (value) {
      this.notchValue = value;
      this.fillDefaultLevel = value;
      this.calculatePositions();
    }
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.calculatePositions();
    });
  }

  ngOnDestroy(): void {
    this.responsiveSubscription?.unsubscribe();
  }

  calculatePositions(): void {
    this.calcLeft =
      this.slider?.nativeElement.getBoundingClientRect().left /
      (this.inGrid ? 1.1 : 1);
    this.calcWidth =
      this.bar?.nativeElement.getBoundingClientRect().width /
      (this.inGrid ? 1.1 : 1);
    this.knobWidth =
      this.knob?.nativeElement.getBoundingClientRect().width /
      (this.inGrid ? 1.1 : 1);
  }

  knobDragStart(evt: MouseEvent | TouchEvent): void {
    if (!this.showKnob) {
      return;
    }
    evt.preventDefault();
    this.calculatePositions();
    this.knobDragActive = true;
    const pageX =
      (evt instanceof MouseEvent ? evt.pageX : evt.touches[0].pageX) /
      (this.inGrid ? 1.1 : 1);
    this.setElementPositions(pageX);
    if (evt instanceof MouseEvent) {
      this.mousemoveListener = this.renderer.listen(
        'window',
        'mousemove',
        (event) => {
          this.knobDragMove(event);
        },
      );
      this.mouseupListener = this.renderer.listen(
        'window',
        'mouseup',
        (event) => {
          this.knobDragEnd(event);
        },
      );
    } else {
      this.mousemoveListener = this.renderer.listen(
        'window',
        'touchmove',
        (event) => {
          this.knobDragMove(event);
        },
      );
      this.mouseupListener = this.renderer.listen(
        'window',
        'touchend',
        (event) => {
          this.knobDragEnd(event);
        },
      );
    }
  }

  knobDragMove(evt: MouseEvent | TouchEvent): void {
    this.checkmarkShown = false;

    if (!this.showKnob) {
      return;
    }
    if (this.knobDragActive) {
      this.checkmarkShown = false;

      const pageX =
        (evt instanceof MouseEvent ? evt.pageX : evt.touches[0].pageX) /
        (this.inGrid ? 1.1 : 1);

      const level = this.setElementPositions(pageX);
      if (this.points) {
        this.activePoint = this.points.find((e) => e.value >= level);
        this.pointSelected.emit(this.activePoint?.value);
      }
      this.dragEnd.emit(level);
    }
  }

  knobDragEnd(evt: MouseEvent | TouchEvent): void {
    if (!this.showKnob) {
      return;
    }
    if (this.knobDragActive) {
      this.knobDragActive = false;
      const pageX =
        (evt instanceof MouseEvent ? evt.pageX : evt.changedTouches[0].pageX) /
        (this.inGrid ? 1.1 : 1);

      const level = this.setElementPositions(pageX);

      if (this.points) {
        this.activePoint = this.points.find((e) => e.value >= level);
        this.pointSelected.emit(this.activePoint?.value);
      }

      this.dragEnd.emit(level);
      this.dragFinished.emit(level);
      this.mousemoveListener();
      this.mouseupListener();
      this.checkmarkShown = true;
    }
  }

  setElementPositions(x: number): number {
    let level = ((x - this.calcLeft) / this.calcWidth) * 10;
    level = Math.max(Math.min(level, 10), 0);

    const steps = 10;
    const stepSize = 10 / steps;

    // snap to nearest step
    const amount = Math.round(level / stepSize) * stepSize;

    this.levelDisplay = amount;
    this.setCurrent(amount);
    this.levelStore = amount;
    this.knobPositionX = (amount / 10) * this.calcWidth - this.knobWidth * 0.6;
    return this.levelDisplay;
  }

  setCurrent(level: number): void {
    this.levelStore = level;
    if (level > this.notchValue) {
      this.fillCurrentLevel = level - this.notchValue;
      this.currentLeft = this.notchValue;
    } else {
      this.fillCurrentLevel = this.notchValue - level;
      this.currentLeft = level;
    }
  }

  initPositions(level: number): void {
    if (level > 10) {
      level = 10;
    }
    this.setCurrent(level);
    this.levelDisplay = Math.round(level);
    this.knobPositionX = (level / 10) * this.calcWidth - this.knobWidth * 0.6;

    if (this.points) {
      this.activePoint = this.points.find((e) => e.value >= this.levelDisplay);
    }
  }

  @HostListener('window:mousemove', ['$event'])
  public mousemove(event: MouseEvent): void {
    // detachChanges
  }
}
